import React, { useEffect, useRef, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Play, Pause, Upload, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { analyzeTrack, snapToBeat } from "@/utils/bpmDetect";
import { useMixer } from "@/context/MixerContext";

const DECK_COLORS = {
  A: { accent: "#00F0FF", glow: "rgba(0,240,255,0.35)" },
  B: { accent: "#FF8C00", glow: "rgba(255,140,0,0.35)" },
  C: { accent: "#00FF66", glow: "rgba(0,255,102,0.35)" },
  D: { accent: "#FF0055", glow: "rgba(255,0,85,0.35)" },
};

export default function Deck({ id, state, setState, onSync }) {
  const { engine, ready, mixerError } = useMixer();
  const audioRef = useRef(null);
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const analyserRef = useRef(null);
  const gainNodeRef = useRef(null);
  const eqNodesRef = useRef(null);
  const sourceNodeRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const [analyzing, setAnalyzing] = useState(false);

  const color = DECK_COLORS[id];

  // Auto-detect BPM/key when track lacks them
  useEffect(() => {
    const t = state.track;
    if (!t || !t.source_id) return;
    if (t.analyzed) return;
    if (t.bpm && t.key) return;
    let cancelled = false;
    setAnalyzing(true);
    analyzeTrack(t.source_id)
      .then(({ bpm, key, keyName, beatPhase }) => {
        if (cancelled) return;
        setState(s => {
          if (!s.track || s.track.id !== t.id) return s;
          return { ...s, track: { ...s.track, bpm: bpm || s.track.bpm, key: key || s.track.key, keyName, beatPhase: beatPhase ?? 0, analyzed: true } };
        });
      })
      .catch(err => {
        console.warn("BPM detect failed", err);
        setState(s => {
          if (!s.track || s.track.id !== t.id) return s;
          return { ...s, track: { ...s.track, analyzed: true } };
        });
      })
      .finally(() => { if (!cancelled) setAnalyzing(false); });
    return () => { cancelled = true; };
  }, [state.track?.id]); // eslint-disable-line

  // Setup Web Audio graph using shared engine
  const setupAudio = useCallback(() => {
    if (!audioRef.current || sourceNodeRef.current || !engine) return;
    const ctx = engine.ctx;
    const src = ctx.createMediaElementSource(audioRef.current);
    sourceNodeRef.current = src;

    const low = ctx.createBiquadFilter(); low.type = "lowshelf"; low.frequency.value = 320;
    const mid = ctx.createBiquadFilter(); mid.type = "peaking"; mid.frequency.value = 1000; mid.Q.value = 0.7;
    const high = ctx.createBiquadFilter(); high.type = "highshelf"; high.frequency.value = 3200;
    eqNodesRef.current = { low, mid, high };

    const gain = ctx.createGain(); gain.gain.value = state.volume;
    gainNodeRef.current = gain;

    const analyser = ctx.createAnalyser();
    analyser.fftSize = 256;
    analyserRef.current = analyser;

    src.connect(low).connect(mid).connect(high).connect(gain).connect(analyser).connect(engine.channelInput(id));
  }, [engine, id, state.volume]);

  // Apply live params
  useEffect(() => {
    if (gainNodeRef.current) gainNodeRef.current.gain.value = state.volume;
    if (eqNodesRef.current) {
      eqNodesRef.current.low.gain.value = (state.eqLow - 0.5) * 30;
      eqNodesRef.current.mid.gain.value = (state.eqMid - 0.5) * 30;
      eqNodesRef.current.high.gain.value = (state.eqHigh - 0.5) * 30;
    }
    if (audioRef.current) {
      audioRef.current.playbackRate = 1 + state.pitch / 100;
    }
  }, [state.volume, state.eqLow, state.eqMid, state.eqHigh, state.pitch]);

  // Waveform rendering
  useEffect(() => {
    const draw = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      const w = canvas.width, h = canvas.height;
      ctx.fillStyle = "#0B0E14";
      ctx.fillRect(0, 0, w, h);
      ctx.strokeStyle = "#1E2638";
      ctx.lineWidth = 1;
      for (let x = 0; x < w; x += 24) {
        ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, h); ctx.stroke();
      }
      if (analyserRef.current && state.playing) {
        const arr = new Uint8Array(analyserRef.current.frequencyBinCount);
        analyserRef.current.getByteFrequencyData(arr);
        const barW = w / arr.length;
        for (let i = 0; i < arr.length; i++) {
          const v = arr[i] / 255;
          const bh = v * h;
          const grad = ctx.createLinearGradient(0, h, 0, h - bh);
          grad.addColorStop(0, color.accent);
          grad.addColorStop(1, "#FFFFFF");
          ctx.fillStyle = grad;
          ctx.fillRect(i * barW, h - bh, barW - 1, bh);
        }
      } else {
        ctx.strokeStyle = color.accent + "55";
        ctx.lineWidth = 2;
        ctx.beginPath();
        for (let x = 0; x < w; x++) {
          const y = h / 2 + Math.sin((x + Date.now() / 50) * 0.05) * (h / 6);
          if (x === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
        }
        ctx.stroke();
      }
      ctx.strokeStyle = color.accent;
      ctx.lineWidth = 2;
      ctx.beginPath(); ctx.moveTo(w / 2, 0); ctx.lineTo(w / 2, h); ctx.stroke();
      rafRef.current = requestAnimationFrame(draw);
    };
    rafRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(rafRef.current);
  }, [state.playing, color.accent]);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onTime = () => { if (a.duration) setProgress((a.currentTime / a.duration) * 100); };
    a.addEventListener("timeupdate", onTime);
    return () => a.removeEventListener("timeupdate", onTime);
  }, [state.track]);

  // Loop enforcement — polls audio.currentTime at 40ms while loop is active
  useEffect(() => {
    const a = audioRef.current;
    const loop = state.loop;
    if (!a || !loop || !loop.bars || loop.start == null || loop.end == null) return;
    const iv = setInterval(() => {
      if (!audioRef.current) return;
      if (audioRef.current.currentTime >= loop.end) {
        audioRef.current.currentTime = loop.start;
      }
    }, 40);
    return () => clearInterval(iv);
  }, [state.loop]);

  // ─────────── MIDI jog wheel bridge (Pioneer + Technics MK7) ───────────
  // Studio.jsx dispatches these CustomEvents from the shared MidiContext
  // whenever a hardware jog wheel is touched or spun. We keep the wiring
  // here because Deck owns the audioRef — Studio only owns state.
  useEffect(() => {
    if (!state.id) return;
    const el = audioRef.current;
    if (!el) return;
    const nudgeTimerRef = { current: null };

    const onTouch = (e) => {
      // While the top platter is touched, pause the audio so the user's
      // scrub input drives currentTime directly. Resume on release if the
      // deck was playing before the touch.
      const touching = !!e.detail?.touching;
      if (touching) {
        el.dataset.wasPlaying = String(!el.paused);
        try { el.pause(); } catch { /* ignore */ }
      } else if (el.dataset.wasPlaying === "true") {
        el.play().catch(() => {});
      }
    };
    const onScrub = (e) => {
      const seconds = Number(e.detail?.seconds || 0);
      if (!Number.isFinite(seconds) || seconds === 0) return;
      const next = Math.max(0, Math.min((el.duration || 1) - 0.05, (el.currentTime || 0) + seconds));
      try { el.currentTime = next; } catch { /* ignore seek errors */ }
    };
    const onNudge = (e) => {
      const rateDelta = Number(e.detail?.rateDelta || 0);
      if (!Number.isFinite(rateDelta) || rateDelta === 0) return;
      const base = el._baseRate || 1;
      const target = Math.max(0.25, Math.min(4, base + rateDelta));
      el.playbackRate = target;
      // Snap back to baseline 250ms after the last tick so pitch nudge feels
      // like a physical wheel — momentary bend, not a permanent shift.
      if (nudgeTimerRef.current) clearTimeout(nudgeTimerRef.current);
      nudgeTimerRef.current = setTimeout(() => { el.playbackRate = base; }, 250);
    };

    const touchEvt = `nd-jog-touch-${state.id}`;
    const scrubEvt = `nd-jog-scrub-${state.id}`;
    const nudgeEvt = `nd-jog-nudge-${state.id}`;
    window.addEventListener(touchEvt, onTouch);
    window.addEventListener(scrubEvt, onScrub);
    window.addEventListener(nudgeEvt, onNudge);
    return () => {
      window.removeEventListener(touchEvt, onTouch);
      window.removeEventListener(scrubEvt, onScrub);
      window.removeEventListener(nudgeEvt, onNudge);
      if (nudgeTimerRef.current) clearTimeout(nudgeTimerRef.current);
    };
  }, [state.id]);

  const togglePlay = async () => {
    // Load-order guardrails — each with a visible reason so a stuck button
    // never looks like "the app is frozen". Real failure modes we've seen
    // in the wild: (a) Web Audio disabled in an embedded WebView, (b)
    // mobile Chrome refusing to resume the AudioContext until a fresh
    // gesture arrives, (c) MediaElementSource init failing when the audio
    // element's blob URL was revoked between load and play.
    if (!state.track) {
      toast.info(`Deck ${id}: load a track from the library first.`);
      return;
    }
    if (mixerError) {
      toast.error(`Web Audio blocked — ${mixerError}. Try a different browser or reload the page.`);
      return;
    }
    if (!ready || !engine) {
      toast.info("Audio engine still starting — tap Play again in a second.");
      return;
    }
    try {
      setupAudio();
    } catch (e) {
      console.error("setupAudio failed:", e);
      toast.error(`Deck ${id}: audio pipeline error — ${e?.message || "unknown"}. Try reloading the page.`);
      return;
    }
    try {
      await engine.resume();
    } catch (e) {
      console.error("engine.resume failed:", e);
      toast.error(`Deck ${id}: audio locked by browser autoplay policy. Tap Play again.`);
      return;
    }
    if (state.playing) {
      audioRef.current.pause();
      setState(s => ({ ...s, playing: false }));
      return;
    }
    try {
      await audioRef.current.play();
      setState(s => ({ ...s, playing: true }));
    } catch (e) {
      // Real errors we've seen: NotAllowedError (browser autoplay policy),
      // NotSupportedError (codec / corrupt file), AbortError (source got
      // swapped mid-play). All three previously looked identical to the
      // user — a Play button that did nothing.
      console.error("audio.play failed:", e);
      const name = e?.name || "";
      let msg;
      if (name === "NotAllowedError") msg = "Browser blocked autoplay — tap Play once more.";
      else if (name === "NotSupportedError") msg = "This audio file's codec isn't supported. Try MP3 or WAV.";
      else if (name === "AbortError") msg = "Playback was interrupted — try again.";
      else msg = e?.message || "Playback failed unexpectedly.";
      toast.error(`Deck ${id}: ${msg}`);
    }
  };

  const onFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    setState(s => ({
      ...s,
      track: { id: `local-${Date.now()}`, title: file.name.replace(/\.[^.]+$/, ""), artist: "Local File", source: "local", source_id: url, bpm: null, key: null },
      playing: false,
      hotCues: [null, null, null, null],
      loop: { bars: 0, start: null, end: null },
    }));
  };

  const triggerHotCue = (idx) => {
    const a = audioRef.current;
    if (!a || !state.track) return;
    const cues = state.hotCues || [null, null, null, null];
    if (cues[idx] == null) {
      const t = state.track.bpm ? snapToBeat(a.currentTime, state.track.bpm, state.track.beatPhase || 0) : a.currentTime;
      const next = [...cues];
      next[idx] = t;
      setState(s => ({ ...s, hotCues: next }));
    } else {
      a.currentTime = cues[idx];
    }
  };

  const clearHotCue = (idx, e) => {
    e.preventDefault();
    const cues = state.hotCues || [null, null, null, null];
    if (cues[idx] == null) return;
    const next = [...cues];
    next[idx] = null;
    setState(s => ({ ...s, hotCues: next }));
  };

  const toggleLoop = (bars) => {
    const a = audioRef.current;
    if (!a || !state.track) return;
    const bpm = state.track.bpm;
    if (!bpm) { return; }
    if (state.loop?.bars === bars) {
      setState(s => ({ ...s, loop: { bars: 0, start: null, end: null } }));
      return;
    }
    // Snap loop start to the nearest downbeat so loops stay on the one
    const start = snapToBeat(a.currentTime, bpm, state.track.beatPhase || 0);
    const beatSec = 60 / (bpm * (1 + state.pitch / 100));
    const end = start + bars * 4 * beatSec;
    setState(s => ({ ...s, loop: { bars, start, end } }));
  };

  return (
    <div
      className="rounded-xl border p-4 relative overflow-hidden bg-[#121620] z-10"
      style={{ borderColor: color.accent + "55", boxShadow: `inset 0 0 60px ${color.glow}` }}
      data-testid={`deck-${id.toLowerCase()}-container`}
    >
      <div className="absolute top-0 left-0 right-0 h-1" style={{ background: color.accent }}/>

      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="font-display font-black text-3xl" style={{ color: color.accent }}>DECK {id}</div>
          <div className="text-xs font-mono text-[#94A3B8] uppercase tracking-widest">{state.track?.source || "empty"}</div>
        </div>
        <div className="text-right">
          <div className="font-mono font-black text-2xl flex items-center justify-end gap-1" data-testid={`deck-${id.toLowerCase()}-bpm-display`}>
            {analyzing && <Loader2 className="w-4 h-4 animate-spin opacity-60"/>}
            {state.track && state.track.bpm ? ((state.track.bpm || 0) * (1 + state.pitch / 100)).toFixed(1) : "--.-"}
          </div>
          <div className="text-[10px] font-mono text-[#94A3B8] uppercase tracking-widest">BPM · {state.track?.key || "--"}</div>
        </div>
      </div>

      <div className="mb-3 min-h-[48px]">
        {state.track ? (
          <div>
            <div className="font-bold truncate">{state.track.title}</div>
            <div className="text-xs text-[#94A3B8] truncate">{state.track.artist}</div>
          </div>
        ) : (
          <div className="text-xs text-[#64748B]">Load a track from the library →</div>
        )}
      </div>

      <canvas ref={canvasRef} width={480} height={80} className="w-full h-20 rounded border border-[#1E2638] mb-3" data-testid={`deck-${id.toLowerCase()}-waveform-canvas`}/>

      <div className="h-1 bg-[#1E2638] rounded-full mb-3 overflow-hidden">
        <div className="h-full transition-all" style={{ width: `${progress}%`, background: color.accent }}/>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-3">
        <Button data-testid={`deck-${id.toLowerCase()}-play-button`} onClick={togglePlay} disabled={!state.track}
          className="col-span-1 h-12 font-black uppercase tracking-widest text-black"
          style={{ background: color.accent }}>
          {state.playing ? <Pause className="w-5 h-5"/> : <Play className="w-5 h-5"/>}
        </Button>
        <Button data-testid={`deck-${id.toLowerCase()}-cue-button`}
          onClick={() => { if (audioRef.current) { audioRef.current.currentTime = 0; } }}
          className="h-12 bg-[#FF2A6D]/20 border border-[#FF2A6D] text-[#FF2A6D] hover:bg-[#FF2A6D] hover:text-white font-black uppercase tracking-widest">
          CUE
        </Button>
        <Button data-testid={`deck-${id.toLowerCase()}-sync-button`} onClick={() => onSync && onSync(id)} disabled={!state.track}
          className="h-12 bg-[#10B981]/20 border border-[#10B981] text-[#10B981] hover:bg-[#10B981] hover:text-black font-black uppercase tracking-widest">
          SYNC
        </Button>
      </div>

      <div className="mb-3">
        <div className="flex justify-between text-[10px] font-mono uppercase tracking-widest text-[#94A3B8] mb-1">
          <span>Pitch</span><span data-testid={`deck-${id.toLowerCase()}-pitch-value`}>{state.pitch > 0 ? "+" : ""}{state.pitch.toFixed(2)}%</span>
        </div>
        <input data-testid={`deck-${id.toLowerCase()}-pitch-fader`} type="range" min={-16} max={16} step={0.1}
          value={state.pitch} onChange={e => setState(s => ({ ...s, pitch: Number(e.target.value) }))}
          className="dj-slider w-full"/>
      </div>

      <div className="grid grid-cols-4 gap-2 mb-2">
        {[
          { label: "LOW", key: "eqLow", testid: "low-eq" },
          { label: "MID", key: "eqMid", testid: "mid-eq" },
          { label: "HIGH", key: "eqHigh", testid: "high-eq" },
          { label: "VOL", key: "volume", testid: "volume-fader" },
        ].map(k => (
          <div key={k.key} className="text-center">
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#94A3B8] mb-1">{k.label}</div>
            <input
              data-testid={`deck-${id.toLowerCase()}-${k.testid}`}
              type="range" min={0} max={1} step={0.01} value={state[k.key]}
              onChange={e => setState(s => ({ ...s, [k.key]: Number(e.target.value) }))}
              className="dj-slider w-full"
            />
          </div>
        ))}
      </div>

      {/* Hot Cues */}
      <div className="mt-3">
        <div className="text-[10px] font-mono uppercase tracking-widest text-[#94A3B8] mb-1">Hot Cues · right-click to clear</div>
        <div className="grid grid-cols-4 gap-1.5">
          {[0, 1, 2, 3].map(idx => {
            const set = (state.hotCues || [])[idx] != null;
            return (
              <button
                key={idx}
                data-testid={`deck-${id.toLowerCase()}-hotcue-${idx + 1}`}
                onClick={() => triggerHotCue(idx)}
                onContextMenu={(e) => clearHotCue(idx, e)}
                disabled={!state.track}
                className="h-9 rounded font-black text-xs uppercase tracking-widest transition-all active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed"
                style={{
                  background: set ? color.accent : "transparent",
                  color: set ? "#000" : color.accent,
                  border: `1.5px solid ${color.accent}`,
                  boxShadow: set ? `0 0 12px ${color.glow}` : "none",
                }}
              >C{idx + 1}</button>
            );
          })}
        </div>
      </div>

      {/* Loop Rolls */}
      <div className="mt-3">
        <div className="text-[10px] font-mono uppercase tracking-widest text-[#94A3B8] mb-1">
          Loop {!state.track?.bpm && <span className="text-[#EF4444]">· needs BPM</span>}
        </div>
        <div className="grid grid-cols-4 gap-1.5">
          {[1, 4, 8, 16].map(bars => {
            const active = state.loop?.bars === bars;
            return (
              <button
                key={bars}
                data-testid={`deck-${id.toLowerCase()}-loop-${bars}`}
                onClick={() => toggleLoop(bars)}
                disabled={!state.track || !state.track.bpm}
                className={`h-9 rounded font-black text-xs uppercase tracking-widest transition-all active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed ${
                  active
                    ? "bg-[#FFC107] text-black shadow-[0_0_12px_rgba(255,193,7,0.5)] animate-pulse"
                    : "bg-transparent border border-[#FFC107] text-[#FFC107] hover:bg-[#FFC107]/10"
                }`}
              >{bars}<span className="text-[9px] opacity-70 ml-0.5">b</span></button>
            );
          })}
        </div>
      </div>

      <label className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-[#94A3B8] hover:text-[#00F0FF] cursor-pointer mt-2">
        <Upload className="w-3 h-3"/>
        <span>Upload local file</span>
        <input data-testid={`deck-${id.toLowerCase()}-upload-input`} type="file" accept="audio/*" onChange={onFileUpload} className="hidden"/>
      </label>

      {state.track?.source_id && (
        <audio ref={audioRef} src={state.track.source_id} crossOrigin="anonymous"
          onEnded={() => setState(s => ({ ...s, playing: false }))}/>
      )}
    </div>
  );
}
